/**
 * @fileoverview Task timing section (MOR-696)
 *
 * Consolidates every date/time control for a Morgan key date / task into one
 * place so the practitioner can reason about scheduling without hunting across
 * the panel:
 *  - **Due** — the hard deadline the row is canonically placed on (date +
 *    optional time slot). All-day by default for statutory rows.
 *  - **Planned** — the *soft* working window (a distinct date + optional time
 *    slot) used for capacity planning. This is deliberately separate from the
 *    due date: a task due on 2 Jul can be planned to be worked on 23 Jun.
 *
 * Because due and planned are independent, the calendar's Due / Planned / Both
 * toggle decides which date each task is drawn on — a planned window only shows
 * on the calendar in "Planned" or "Both" mode, on its own planned date.
 *
 * Due-date / time edits write through `keyDates.update` (persisted rows) or
 * `keyDates.rescheduleDerived` (derived statutory rows, by code). Planned-field
 * edits write through `tasks.updateTask`, which materialises a derived row on
 * first touch.
 */

import { useQuery, useMutation } from "convex/react";
import { CalendarClock, Clock, Target } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { Label } from "@/ui/primitives/label";
import { Switch } from "@/ui/primitives/switch";
import { Button } from "@/ui/primitives/button";
import { DatePicker } from "@/ui/composed/inputs/date-picker";
import { TimePicker } from "@/ui/composed/inputs/time-picker";
import { extractErrorMessage } from "@/utils/errors";

import { isEventKind, type CalendarEventRow } from "@/calendar/eventAdapter";

/** Either an existing persisted id, or a (jobId, code) pair to materialise. */
type TaskTarget =
  { id: Id<"jobKeyDates"> } | { jobId: Id<"jobs">; code: string } | null;

function resolveTarget(event: CalendarEventRow): TaskTarget {
  if (event.persistedId) return { id: event.persistedId };
  if (event.code) return { jobId: event.jobId, code: event.code };
  return null;
}

export function TaskTimingSection({ event }: { event: CalendarEventRow }) {
  const updateKeyDate = useMutation(api.keyDates.mutations.update);
  const rescheduleDerived = useMutation(
    api.keyDates.mutations.rescheduleDerived,
  );
  const updateTask = useMutation(api.tasks.mutations.updateTask);
  const materialise = useMutation(api.keyDates.mutations.materialise);

  // Org members are not needed here, but the live job query keeps the section
  // reactive to changes made elsewhere in the panel.
  useQuery(api.keyDates.queries.listForJob, { jobId: event.jobId });

  const target = resolveTarget(event);

  // The all-day / time-slot controls write to `keyDates.update`, which needs a
  // persisted row id. Derived statutory rows have none until first touched, so
  // materialise on demand — the same promotion that setting a status/planned
  // date already performs — then patch the resulting row.
  const ensureId = async (): Promise<Id<"jobKeyDates"> | null> => {
    if (event.persistedId) return event.persistedId;
    if (event.code) {
      return await materialise({ jobId: event.jobId, code: event.code });
    }
    return null;
  };

  // ---- due-date helpers --------------------------------------------------
  const guardedDue = async (fn: () => Promise<unknown>, fallback: string) => {
    try {
      await fn();
    } catch (e) {
      toast.error(extractErrorMessage(e, fallback));
    }
  };

  const onDueDate = (value: string) =>
    guardedDue(async () => {
      if (!value) return;
      if (event.persistedId) {
        await updateKeyDate({ id: event.persistedId, date: value });
      } else if (event.code) {
        await rescheduleDerived({
          jobId: event.jobId,
          code: event.code,
          date: value,
        });
      }
    }, "Could not update the due date.");

  const onDueAllDay = (checked: boolean) =>
    guardedDue(async () => {
      const id = await ensureId();
      if (!id) return;
      await updateKeyDate({
        id,
        allDay: checked,
        startTime: checked ? undefined : event.startTime || "09:00",
        endTime: checked ? undefined : event.endTime || "10:00",
      });
    }, "Could not update the due time.");

  const onDueTime = (patch: { startTime?: string; endTime?: string }) =>
    guardedDue(async () => {
      const id = await ensureId();
      if (!id) return;
      await updateKeyDate({ id, ...patch });
    }, "Could not update the due time.");

  // ---- planned-window helpers -------------------------------------------
  const onPlanned = (patch: Record<string, unknown>, fallback: string) =>
    guardedDue(() => {
      if (!target) return Promise.resolve();
      return updateTask({
        ...target,
        patch,
      } as Parameters<typeof updateTask>[0]);
    }, fallback);

  const isEvent = isEventKind(event);

  return (
    <div className="space-y-6 text-sm">
      {/* --------------------------------------------------------------- */}
      {/* Timing (events) / Due date (tasks)                               */}
      {/* --------------------------------------------------------------- */}
      <div className="space-y-3">
        <h4 className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
          <CalendarClock className="size-3.5" />
          {isEvent ? "Timing" : "Due"}
        </h4>
        <p className="text-xs text-muted-foreground">
          {isEvent
            ? "When this event happens. This is the date it's placed on in the calendar."
            : "The hard deadline — the date the task is placed on in the calendar's Due view."}
        </p>

        <div className="grid grid-cols-[64px_1fr] items-center gap-2">
          <Label className="text-xs text-muted-foreground">Date</Label>
          <DatePicker value={event.date} onChange={onDueDate} />
        </div>

        {/* All-day + timeslot for events (including derived rows, which
            materialise on first edit); tasks carry a due date only. */}
        {isEvent && (
          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <Label className="font-medium">All day</Label>
              <p className="text-xs text-muted-foreground">
                Toggle off to set a specific time slot.
              </p>
            </div>
            <Switch checked={event.allDay} onCheckedChange={onDueAllDay} />
          </div>
        )}

        {isEvent && !event.allDay && (
          <div className="flex items-center gap-1.5">
            <Clock className="size-4 shrink-0 text-muted-foreground" />
            <TimePicker
              value={event.startTime ?? "09:00"}
              className="w-32"
              inputClassName="h-8 w-full pl-8 pr-2 py-1 text-sm border border-input rounded-md bg-background tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onChange={(v) => v && onDueTime({ startTime: v })}
            />
            <span className="text-xs text-muted-foreground">–</span>
            <TimePicker
              value={event.endTime ?? "10:00"}
              className="w-32"
              inputClassName="h-8 w-full pl-8 pr-2 py-1 text-sm border border-input rounded-md bg-background tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onChange={(v) => v && onDueTime({ endTime: v })}
            />
          </div>
        )}
      </div>

      {/* --------------------------------------------------------------- */}
      {/* Planned window — the soft working window, for events and tasks.  */}
      {/* --------------------------------------------------------------- */}
      <div className="space-y-3 border-t pt-5">
        <h4 className="flex items-center gap-1.5 font-sans text-xs font-semibold uppercase tracking-wider text-foreground">
          <Target className="size-3.5" />
          Planned
        </h4>
        <p className="text-xs text-muted-foreground">
          The soft window you intend to work on this — can be a different date
          to the due date. Shows on the calendar's{" "}
          <span className="font-medium">Planned</span> or{" "}
          <span className="font-medium">Both</span> view.
        </p>

        <div className="grid grid-cols-[64px_1fr] items-center gap-2">
          <Label className="text-xs text-muted-foreground">From</Label>
          <DatePicker
            value={event.plannedDate ?? ""}
            placeholder="Not planned"
            onChange={(v) => {
              // Clearing the start also clears the end so we never leave a
              // dangling "planned to" without a start of the window.
              const patch: Record<string, string | null> = {
                plannedDate: v || null,
              };
              if (!v) patch.plannedEndDate = null;
              onPlanned(patch, "Could not set planned date.");
            }}
          />
        </div>

        {event.plannedDate && (
          <>
            <div className="grid grid-cols-[64px_1fr] items-center gap-2">
              <Label className="text-xs text-muted-foreground">To</Label>
              <div className="flex items-center gap-1.5">
                <DatePicker
                  value={event.plannedEndDate ?? ""}
                  placeholder="Same day"
                  className="min-w-0 flex-1"
                  onChange={(v) =>
                    onPlanned(
                      { plannedEndDate: v || null },
                      "Could not set planned end date.",
                    )
                  }
                />
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 px-2 text-xs text-muted-foreground"
                  onClick={() =>
                    onPlanned(
                      { plannedDate: null, plannedEndDate: null },
                      "Could not clear planned date.",
                    )
                  }
                >
                  Clear
                </Button>
              </div>
            </div>

            {/* All-day vs. blocked-out time for the planned window. */}
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label className="font-medium">All day</Label>
                <p className="text-xs text-muted-foreground">
                  Toggle off to block out a specific time slot.
                </p>
              </div>
              <Switch
                checked={event.plannedAllDay}
                onCheckedChange={(checked) =>
                  onPlanned(
                    {
                      plannedAllDay: checked,
                      ...(checked
                        ? {}
                        : {
                            plannedStartTime: event.plannedStartTime || "09:00",
                            plannedEndTime: event.plannedEndTime || "10:00",
                          }),
                    },
                    "Could not update planned time.",
                  )
                }
              />
            </div>

            {!event.plannedAllDay && (
              <div className="flex items-center gap-1.5">
                <Clock className="size-4 shrink-0 text-muted-foreground" />
                <TimePicker
                  value={event.plannedStartTime ?? "09:00"}
                  className="w-32"
                  inputClassName="h-8 w-full pl-8 pr-2 py-1 text-sm border border-input rounded-md bg-background tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onChange={(v) =>
                    onPlanned(
                      { plannedStartTime: v || null },
                      "Could not set planned start time.",
                    )
                  }
                />
                <span className="text-xs text-muted-foreground">–</span>
                <TimePicker
                  value={event.plannedEndTime ?? "10:00"}
                  className="w-32"
                  inputClassName="h-8 w-full pl-8 pr-2 py-1 text-sm border border-input rounded-md bg-background tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  onChange={(v) =>
                    onPlanned(
                      { plannedEndTime: v || null },
                      "Could not set planned end time.",
                    )
                  }
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
